'use client';

import { UserSupportTicketCategory } from '@trucksmarter/shared';
import { useState } from 'react';
import DallasButton from '../Dallas/Buttons/DallasButton/DallasButton';
import DallasDialog from '../Dallas/Dialogs/DallasDialog';
import DallasStack from '../Dallas/Stack/DallasStack';
import DallasText from '../Dallas/Text/DallasText';
import DallasImageHappyPeaceSign from '../Dallas/Images/HappyPeaceSign/DallasImageHappyPeaceSign';
import CreateSupportTicketDialogFormContent from './CreateSupportTicketDialogFormContent';
import createSupportTicket from '../../_lib/api/client/create-support-ticket';
import createBrokerUserSupportTicket from '../../_lib/api/client/broker/create-broker-user-support-ticket';
import {
  BROKER_SUPPORT_TICKET_SUBJECT_OPTIONS,
  BrokerSupportTicketSubject,
  SUPPORT_TICKET_CATEGORY_OPTIONS,
} from './user-support-ticket';

import styles from './CreateSupportTicketDialog.module.scss';

interface Props {
  onClose: () => void;
  isBrokerUser?: boolean;
}

export default function CreateSupportTicketDialog({
  onClose,
  isBrokerUser,
}: Props) {
  const [isSubmittedSuccessful, setIsSubmittedSuccessful] = useState(false);

  return (
    <DallasDialog
      className={styles.root}
      onClose={onClose}
      padding="xl"
      gap={isSubmittedSuccessful ? 'xl' : '2xl'}
      content={
        isSubmittedSuccessful ? (
          <SupportTicketSuccessfulSubmit
            forBrokerUser={isBrokerUser}
            onClose={onClose}
          />
        ) : (
          <>
            <DallasStack gap="2xs">
              <DallasText variant="headerRegularS">Submit an issue</DallasText>
              <DallasText color="darkGray" as="p">
                Are you having trouble with TruckSmarter? Tell us about the
                issue you are facing so our team can help you. Or visit our Help
                Center.
              </DallasText>
            </DallasStack>
            {isBrokerUser ? (
              <CreateSupportTicketDialogFormContent<BrokerSupportTicketSubject>
                onClose={onClose}
                setIsSubmittedSuccessful={setIsSubmittedSuccessful}
                createSupportTicket={createBrokerUserSupportTicket}
                options={BROKER_SUPPORT_TICKET_SUBJECT_OPTIONS}
              />
            ) : (
              <CreateSupportTicketDialogFormContent<UserSupportTicketCategory>
                onClose={onClose}
                setIsSubmittedSuccessful={setIsSubmittedSuccessful}
                createSupportTicket={createSupportTicket}
                options={SUPPORT_TICKET_CATEGORY_OPTIONS}
              />
            )}
          </>
        )
      }
    />
  );
}

interface SupportTicketSuccessfulSubmitProps {
  forBrokerUser?: boolean;
  onClose: () => void;
}

export function SupportTicketSuccessfulSubmit({
  forBrokerUser = false,
  onClose,
}: SupportTicketSuccessfulSubmitProps) {
  return (
    <>
      <DallasStack alignHorizontal="center">
        <DallasText variant="headerRegularS" align="center">
          Thanks for reaching out!
        </DallasText>
      </DallasStack>
      <DallasStack paddingVertical="2xl" alignHorizontal="center">
        <DallasImageHappyPeaceSign className={styles.happyPeaceSignImage} />
      </DallasStack>
      <DallasStack alignHorizontal="center">
        <DallasText color="darkGray" as="p" align="center">
          {`We are committed to building a better experience for ${
            forBrokerUser ? 'brokers' : 'truck drivers'
          } like you. `}
          We&apos;ll review your request and get back to you!
        </DallasText>
        <DallasButton label="Done" color="primary" onClick={onClose} />
      </DallasStack>
    </>
  );
}
